import {Weekday, Weekend, ResultCategory, Intake, RouteName} from '@/enums';
import {getDanishWeekDays, t} from '@/utils';

export type InfoData = {
	id: number;
	label: string;
	translationString?: TranslationString;
};

export type InfoPreQuestionData = {
	title: string;
	subTitle: string;
};

export type DomainData = InfoData & {
	chapters: any;
};

export type ChapterData = InfoData & {
	themes: any;
};

export type ThemeData = InfoData & {
	questions: any;
	prioritizedItemIds: number[];
};

export type QuestionData = InfoData & {
	reflection: string;
	gate: GateRequirement;
	index: number;
};

export type CategoryData = InfoData & {
	itemIds: string[];
	prioritizationLevel: number | null;
};

export type ProductData = InfoData & {
	unit: string;
	includedInCalculation: boolean;
	portionSize: number;
	maxUnitQuantities: number;
	portions: PortionData[];
};

export type PortionData = {
	weight: number;
	label: string;
	imageUrl: string;
};

export type PortionInput = {
	index: number;
	multiplier: number;
};

export type ProductInput = {
	questionId: number;
	data: ProductData;
	selectedPortions: {
		[Weekday.MONDAY]: PortionInput;
		[Weekday.TUESDAY]: PortionInput;
		[Weekday.WEDNESDAY]: PortionInput;
		[Weekday.THURSDAY]: PortionInput;
		[Weekday.FRIDAY]: PortionInput;
		[Weekday.SATURDAY]: PortionInput;
		[Weekday.SUNDAY]: PortionInput;
	};
};

export type IntakeAmount = {
	[Intake.FOOD]: number;
	[Intake.DRINKS]: number;
	[Intake.FOOD_PORTIONS]: number;
	[Intake.DRINKS_PORTIONS]: number;
};

export type IntakeResult = {
	[Intake.FOOD]: ResultCategory;
	[Intake.DRINKS]: ResultCategory;
};

export type GateData = InfoData & {
	allDaysByDefault: boolean;
};

export interface GateAnswerData {
	_id: number;
	selectedDays: string[];
}

export type SpecialEventOption = {
	id: string;
	value: string;
}

export class GateAnswer {
	private _id: number;
	public selectedDays: string[];

	public get id(): number {
		return this._id;
	}

	public get minOneSelected(): boolean {
		return this.selectedDays.length > 0;
	}

	public get minOneDeselected(): boolean {
		return this.selectedDays.length < getDanishWeekDays().length;
	}

	constructor(data?: GateData, gateAnswerData?: GateAnswerData) {

		if (gateAnswerData) {
			this._id = gateAnswerData._id;
			this.selectedDays = gateAnswerData.selectedDays;
			return;
		}

		if (data) {
			this._id = data.id;
			if (data.allDaysByDefault) {
				this.selectedDays = getDanishWeekDays();
			} else {
				this.selectedDays = [];
			}
			return;
		}

		console.error('GateAnswer contructor. Gate answer not correctly made and initialized with default values');

		this._id = -1;
		this.selectedDays = [];
	}
}

export type GateRequirement = {
	id: number;
	value: boolean;
};

export type RegistrationPeriod = {
	weekNumber: number;
	startDate: string;
	endDate: string;
};

export type UrlList = {
	baseUrl: string;
	defaultImageUrl: string;
};

export type RouteListItem = {
	label: string;
	routeName: RouteName;
};

export type GenderOptionItem = {
	label: string;
	value: string;
};

export type GenderOptions = {
	female: GenderOptionItem;
	male: GenderOptionItem;
	other: GenderOptionItem;
};

export type TranslationString = {
	categoryId: string;
	messageId: string;
}

export type PreQuestionsData = InfoPreQuestionData & {
	questions: PreQuestion[];
};

export type PreQuestion = {
	id: string;
	question: string;
	answer: boolean;
};

export class PreQuestionList implements InfoPreQuestionData {
	private readonly _title: string;
	private readonly _subTitle: string;
	private readonly _questions: PreQuestion[];

	public get title(): string {
		return this._title;
	}

	public get subTitle(): string {
		if (this._subTitle) {
			return this._subTitle;
		} else {
			return 'N/A';
		}
	}

	public get questions(): PreQuestion[] {
		return this._questions;
	}

	constructor(data: PreQuestionsData) {
		this._title = data.title;
		this._subTitle = data.subTitle;
		this._questions = [];
		for (const question of data.questions) {
			this._questions.push({
				id: question.id,
				question: question.question,
				answer: false,
			});
		}
	}
}
